// Turns raw rm_activity_events rows (fetched from the backend) into the
// numbers the dashboard shows. Every card and table below is one of these
// functions run against the same array of events — nothing is hardcoded.

import type { UpdatedStatus, RmActivityEvent } from './types';

const ACHT_THRESHOLDS = {
  notConnected: 90, // 1m 30s
  callBack: 900, // 15m 00s
  notInterested: 1080, // 18m 00s
  trial: 1500, // 25m 00s
};

// A "breach" is disposition-specific — each disposition has its own SLA for
// how long a call is allowed to take before it's flagged. Distinct from
// ACHT_THRESHOLDS above (which only colors the average-handling-time cards).
const BREACH_THRESHOLDS: Record<UpdatedStatus, number> = {
  NOT_CONNECTED: 210, // 3m 30s
  CALL_BACK: 1080, // 18m 00s
  NOT_INTERESTED: 900, // 15m 00s
  TRIAL_ACTIVATED: 1500, // 25m 00s
};
export { BREACH_THRESHOLDS };

// a call with no disposition can't be checked against a threshold, so it
// can't be a breach either. Same for a call with no MEASURED duration
// (duration_seconds: null — the server couldn't find a trustworthy session
// for it, see record_lead_touch_event's docstring) — reporting "not a
// breach" would be lying about something we simply don't know, not a real
// finding, so it's excluded rather than defaulted to "fine".
export const isBreachedCall = (call: { updatedStatus: UpdatedStatus | null; durationSeconds: number | null }): boolean =>
  call.updatedStatus !== null && call.durationSeconds !== null && call.durationSeconds >= BREACH_THRESHOLDS[call.updatedStatus];

// a "full" reference shift, used only to project a pace/occupancy bar
const STANDARD_SHIFT_MINUTES = 600; // 10 hours

// rm_user_id -> that RM's target, already summed by the backend across
// whatever date range is selected (day-by-day: an explicit per-day
// override where a manager set one, else that RM's standing DAILY_TARGET —
// see useRmDailyTargets / get_rm_daily_targets_sum). An RM absent from this
// map has nothing configured at all, which reads as 0.
export type DailyTargetsByRm = Record<string, number>;
const targetFor = (targetsByRm: DailyTargetsByRm, rmUserId: string) => targetsByRm[rmUserId] ?? 0;

// "—" when there's no target to compare against — an unset target must not
// silently read as achieved/0 (Infinity% or NaN%, depending on achieved)
export function formatVsTarget(achieved: number, target: number): string {
  return target ? `${((achieved / target) * 100).toFixed(1)}%` : '—';
}

const isCallTouch = (e: RmActivityEvent) => e.eventType === 'CALL_TOUCH';
const isClosedCallTouch = (e: RmActivityEvent) => isCallTouch(e) && e.endedAt !== null;

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  return `${minutes}m ${String(remainingSeconds).padStart(2, '0')}s`;
}

function formatHours(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = Math.round(minutes % 60);
  return hours > 0 ? `${hours}h ${String(remainingMinutes).padStart(2, '0')}m` : `${remainingMinutes}m`;
}

function average(values: number[]): number {
  return values.length ? values.reduce((sum, v) => sum + v, 0) / values.length : 0;
}

// Strips out calls with no measured duration (duration_seconds: null)
// before an average is computed — counting them as 0 would silently pull
// every average down toward zero, including the denominator (a call we
// have no data for would count as "a confirmed 0-second call" otherwise,
// which is not what it means). A sum is unaffected either way (adding
// nothing is adding nothing), so this is only needed ahead of average().
function measuredDurations(calls: { durationSeconds: number | null }[]): number[] {
  return calls.map((c) => c.durationSeconds).filter((d): d is number => d !== null);
}

// An RM can legitimately have two leads' sessions open at once (two tabs —
// see record_lead_opened in pyro-backend, which keeps every lead's session
// independent rather than evicting one when another opens). Each one's own
// duration is real, but a naive SUM of all of them double-counts whatever
// time they actually overlapped — that's the "handling time > login time"
// bug. This merges overlapping [started, ended] windows into islands first
// (classic interval-merge: sort by start, extend the current island
// whenever the next one starts before it ends, otherwise close it and
// start a new one) and sums the islands instead — real wall-clock time at
// least one lead was open, which can never exceed the actual shift length,
// regardless of how many leads overlapped or by how much.
function mergedIntervalMinutes(calls: { startedAt: string; endedAt: string | null }[]): number {
  const intervals = calls
    .map((c) => ({ start: new Date(c.startedAt).getTime(), end: c.endedAt ? new Date(c.endedAt).getTime() : NaN }))
    .filter((i) => Number.isFinite(i.start) && Number.isFinite(i.end))
    .sort((a, b) => a.start - b.start);

  let totalMs = 0;
  let islandStart: number | null = null;
  let islandEnd = 0;

  for (const { start, end } of intervals) {
    if (islandStart === null) {
      islandStart = start;
      islandEnd = end;
    } else if (start <= islandEnd) {
      // overlaps (or is back-to-back with) the current island — extend it
      islandEnd = Math.max(islandEnd, end);
    } else {
      totalMs += islandEnd - islandStart;
      islandStart = start;
      islandEnd = end;
    }
  }
  if (islandStart !== null) totalMs += islandEnd - islandStart;

  return totalMs / 60000;
}

// groups events by RM, keeping each RM's own events in the order they happened
function groupByRm(events: RmActivityEvent[]): Map<string, RmActivityEvent[]> {
  const byRm = new Map<string, RmActivityEvent[]>();
  events.forEach((event) => {
    const list = byRm.get(event.rmUserId);
    if (list) list.push(event);
    else byRm.set(event.rmUserId, [event]);
  });
  return byRm;
}

// a lead's "current disposition" is whatever its most recent touch says —
// matches how the dashboard note explains attempts-per-lead vs. unique leads
function latestTouchPerLead(calls: RmActivityEvent[]): RmActivityEvent[] {
  const latestById = new Map<number, RmActivityEvent>();
  calls.forEach((call) => {
    if (call.leadRecordId == null) return;
    const current = latestById.get(call.leadRecordId);
    if (!current || call.startedAt > current.startedAt) latestById.set(call.leadRecordId, call);
  });
  return [...latestById.values()];
}

// distinct real leads only — a null leadRecordId (shouldn't normally happen,
// but isn't a lead) must not collapse into a single counted "unique lead",
// or the 4 disposition rates stop summing to 100%
function uniqueLeadCount(calls: RmActivityEvent[]): number {
  const ids = new Set(calls.map((c) => c.leadRecordId).filter((id): id is number => id != null));
  return ids.size;
}

function countByDisposition(calls: RmActivityEvent[]): Record<UpdatedStatus, number> {
  const counts: Record<UpdatedStatus, number> = {
    NOT_CONNECTED: 0,
    CALL_BACK: 0,
    NOT_INTERESTED: 0,
    TRIAL_ACTIVATED: 0,
  };
  calls.forEach((call) => {
    if (call.updatedStatus) counts[call.updatedStatus] += 1;
  });
  return counts;
}

// ---- shift timing (login / break / who's doing what right now) ----

interface ShiftTiming {
  loginMinutes: number;
  status: 'On lead' | 'Off lead' | 'Idle';
  statusMinutes: number;
}

// local calendar day key — matches dateRange.ts's local-clock day boundaries
// (Today/Yesterday/etc. are computed off the browser's local time too)
function calendarDayKey(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}
const isToday = (iso: string) => calendarDayKey(iso) === calendarDayKey(new Date().toISOString());

function groupByCalendarDay(events: RmActivityEvent[]): RmActivityEvent[][] {
  const byDay = new Map<string, RmActivityEvent[]>();
  events.forEach((e) => {
    const key = calendarDayKey(e.startedAt);
    const list = byDay.get(key);
    if (list) list.push(e);
    else byDay.set(key, [e]);
  });
  return [...byDay.values()];
}

// A "shift" only makes sense within one calendar day — summing timestamps
// across a multi-day range (Last 7/30/Custom) as if it were one continuous
// shift turns "login hours" into wall-clock time including the overnight
// gaps between days, which wrecks occupancy (handling ÷ login) too. So each
// day is timed independently and the results are summed; live Status is
// only ever read off *today's* bucket — a call or break left open on a past
// day is a data gap, not something still running.
//
// Break time itself isn't computed here: BREAK_START/BREAK_END aren't
// written by any real flow yet (see buildAdherenceRow/computeMyShiftSnapshot,
// which derive it as loginMinutes − handlingMinutes instead). The BREAK_START/
// BREAK_END scan below exists only to detect a currently-open break for the
// live "Off lead" status, in case that flow starts writing them.
function computeShiftTiming(rmEvents: RmActivityEvent[]): ShiftTiming {
  const now = Date.now();
  let loginMinutes = 0;
  let status: ShiftTiming['status'] = 'Idle';
  let statusMinutes = 0;

  groupByCalendarDay(rmEvents).forEach((dayEvents) => {
    const timestamps: number[] = [];
    dayEvents.forEach((e) => {
      timestamps.push(new Date(e.startedAt).getTime());
      if (e.endedAt) timestamps.push(new Date(e.endedAt).getTime());
    });
    loginMinutes += (Math.max(...timestamps) - Math.min(...timestamps)) / 60_000;

    if (!isToday(dayEvents[0].startedAt)) return; // live status only ever comes from today

    // sequential pairing, chronological order: a BREAK_END is consumed by
    // the nearest still-open BREAK_START and can't be reused for a second
    // one — just to know whether a break is open right now, not to sum duration
    const chrono = [...dayEvents].sort(
      (a, b) => new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime()
    );
    let openBreakStartMs: number | null = null;
    chrono.forEach((e) => {
      if (e.eventType === 'BREAK_START' && openBreakStartMs === null) {
        openBreakStartMs = new Date(e.startedAt).getTime();
      } else if (e.eventType === 'BREAK_END' && openBreakStartMs !== null) {
        openBreakStartMs = null;
      }
    });

    const openCall = dayEvents.find((e) => isCallTouch(e) && e.endedAt === null);
    if (openCall) {
      status = 'On lead';
      statusMinutes = Math.round((now - new Date(openCall.startedAt).getTime()) / 60_000);
    } else if (openBreakStartMs !== null) {
      status = 'Off lead';
      statusMinutes = Math.round((now - openBreakStartMs) / 60_000);
    }
  });

  return { loginMinutes, status, statusMinutes };
}

// ---- Performance tab ----

export interface RmPerformanceRow {
  rmUserId: string;
  name: string;
  manager: string;
  team: string;
  state: string;
  uniqueLeads: number;
  touches: number;
  attemptsPerLead: number;
  notConnectedRate: number;
  callBackRate: number;
  notInterestedRate: number;
  trialRate: number;
  achieved: number;
  target: number;
}

function buildPerformanceRow(
  rmUserId: string,
  rmEvents: RmActivityEvent[],
  targetsByRm: DailyTargetsByRm
): RmPerformanceRow {
  const first = rmEvents[0];
  // only finished calls have a settled outcome — a call still in progress
  // can't be classified into a disposition yet, so it doesn't count here
  const calls = rmEvents.filter(isClosedCallTouch);
  const uniqueLeads = uniqueLeadCount(calls);
  const touches = calls.length;
  const counts = countByDisposition(latestTouchPerLead(calls));
  const pct = (n: number) => (uniqueLeads ? Math.round((n / uniqueLeads) * 1000) / 10 : 0);

  return {
    rmUserId,
    name: first.rmName,
    manager: first.managerName,
    team: first.team,
    state: first.state,
    uniqueLeads,
    touches,
    attemptsPerLead: uniqueLeads ? Math.round((touches / uniqueLeads) * 100) / 100 : 0,
    notConnectedRate: pct(counts.NOT_CONNECTED),
    callBackRate: pct(counts.CALL_BACK),
    notInterestedRate: pct(counts.NOT_INTERESTED),
    trialRate: pct(counts.TRIAL_ACTIVATED),
    achieved: counts.TRIAL_ACTIVATED,
    target: targetFor(targetsByRm, rmUserId),
  };
}

export function computePerformanceByRm(
  events: RmActivityEvent[],
  targetsByRm: DailyTargetsByRm = {}
): RmPerformanceRow[] {
  return [...groupByRm(events)].map(([rmUserId, rmEvents]) => buildPerformanceRow(rmUserId, rmEvents, targetsByRm));
}

export function computeTeamTotals(events: RmActivityEvent[], targetsByRm: DailyTargetsByRm = {}) {
  // same rule as the per-RM rows: a call still in progress has no outcome yet
  const calls = events.filter(isClosedCallTouch);
  const uniqueLeads = uniqueLeadCount(calls);
  const touches = calls.length;
  const counts = countByDisposition(latestTouchPerLead(calls));
  const achieved = counts.TRIAL_ACTIVATED;
  // sum of each RM's own (already range-summed) target, for whichever RMs
  // were actually active (have events) in this range — not every RM in the tenant
  const activeRmIds = [...groupByRm(events).keys()];
  const target = activeRmIds.reduce((sum, rmUserId) => sum + targetFor(targetsByRm, rmUserId), 0);
  const pct = (n: number) => (uniqueLeads ? Math.round((n / uniqueLeads) * 1000) / 10 : 0);

  return {
    uniqueLeadsHandled: uniqueLeads,
    touches,
    attemptsPerLead: uniqueLeads ? Math.round((touches / uniqueLeads) * 100) / 100 : 0,
    achieved,
    target,
    trialActivationRate: pct(achieved),
    notConnectedRate: { rate: pct(counts.NOT_CONNECTED), leads: counts.NOT_CONNECTED },
    callBackRate: { rate: pct(counts.CALL_BACK), leads: counts.CALL_BACK },
    notInterestedRate: { rate: pct(counts.NOT_INTERESTED), leads: counts.NOT_INTERESTED },
    trialSubscribedRate: { rate: pct(achieved), leads: achieved },
  };
}

// ---- Adherence tab ----

export interface RmAdherenceRow {
  rmUserId: string;
  name: string;
  manager: string;
  team: string;
  state: string;
  status: 'On lead' | 'Off lead' | 'Idle';
  statusMinutes: number;
  loginHours: string;
  // raw minutes/seconds behind the formatted *Hours/acht strings above —
  // "2h 05m" vs "10h 02m" sorts wrong as text, so the By RM table's column
  // sort needs these numeric values instead
  loginMinutes: number;
  handlingHours: string;
  handlingMinutes: number;
  breakTime: string;
  breakMinutes: number;
  occupancy: number;
  touches: number;
  acht: string;
  achtSeconds: number;
  notConnectedTime: number;
  notConnectedLabel: string;
  callBackTime: number;
  callBackLabel: string;
  notInterestedTime: number;
  notInterestedLabel: string;
  trialTime: number;
  trialLabel: string;
  breaches: number;
}

function buildAdherenceRow(rmUserId: string, rmEvents: RmActivityEvent[]): RmAdherenceRow {
  const first = rmEvents[0];
  const timing = computeShiftTiming(rmEvents);

  const calls = rmEvents.filter(isCallTouch);
  const closedCalls = calls.filter(isClosedCallTouch);

  const handlingMinutes = mergedIntervalMinutes(closedCalls);
  const occupancy = timing.loginMinutes ? Math.round((handlingMinutes / timing.loginMinutes) * 100) : 0;
  // break = whatever part of the logged-in span wasn't spent handling calls.
  // BREAK_START/BREAK_END events aren't written by any real flow yet, so
  // pairing them would always read 0 — this residual is the only signal
  // actually available for "how much of the shift wasn't active call time"
  const breakMinutes = Math.max(0, timing.loginMinutes - handlingMinutes);

  const secondsFor = (updatedStatus: UpdatedStatus) =>
    measuredDurations(closedCalls.filter((c) => c.updatedStatus === updatedStatus));
  const notConnectedTime = Math.round(average(secondsFor('NOT_CONNECTED')));
  const callBackTime = Math.round(average(secondsFor('CALL_BACK')));
  const notInterestedTime = Math.round(average(secondsFor('NOT_INTERESTED')));
  const trialTime = Math.round(average(secondsFor('TRIAL_ACTIVATED')));

  return {
    rmUserId,
    name: first.rmName,
    manager: first.managerName,
    team: first.team,
    state: first.state,
    status: timing.status,
    statusMinutes: timing.statusMinutes,
    loginHours: formatHours(timing.loginMinutes),
    loginMinutes: timing.loginMinutes,
    handlingHours: formatHours(handlingMinutes),
    handlingMinutes,
    breakTime: formatHours(breakMinutes),
    breakMinutes,
    occupancy,
    touches: closedCalls.length, // matches the Performance tab: "touches" means finished calls, "open" is separate
    acht: formatDuration(average(measuredDurations(closedCalls))),
    achtSeconds: average(measuredDurations(closedCalls)),
    notConnectedTime,
    notConnectedLabel: formatDuration(notConnectedTime),
    callBackTime,
    callBackLabel: formatDuration(callBackTime),
    notInterestedTime,
    notInterestedLabel: formatDuration(notInterestedTime),
    trialTime,
    trialLabel: formatDuration(trialTime),
    // each disposition has its own breach threshold (see BREACH_THRESHOLDS)
    // — >= matches the touch report's own breach flag, so a call landing
    // exactly on its threshold counts as a breach in both places, not just one
    breaches: closedCalls.filter(isBreachedCall).length,
  };
}

export function computeAdherenceByRm(events: RmActivityEvent[]): RmAdherenceRow[] {
  return [...groupByRm(events)].map(([rmUserId, rmEvents]) => buildAdherenceRow(rmUserId, rmEvents));
}

export function computeShiftTimeAverages(events: RmActivityEvent[], rangeLabel = 'Today') {
  const rows = computeAdherenceByRm(events);
  const n = rows.length || 1;

  const totalLoginMinutes = rows.reduce((sum, r) => sum + hoursLabelToMinutes(r.loginHours), 0);
  const totalHandlingMinutes = rows.reduce((sum, r) => sum + hoursLabelToMinutes(r.handlingHours), 0);
  const totalBreakMinutes = rows.reduce((sum, r) => sum + hoursLabelToMinutes(r.breakTime), 0);
  const avgLoginMinutes = totalLoginMinutes / n;
  const avgHandlingMinutes = totalHandlingMinutes / n;
  const avgBreakMinutes = totalBreakMinutes / n;

  const occupancyFraction = avgLoginMinutes ? avgHandlingMinutes / avgLoginMinutes : 0;
  const projectedFullShiftMinutes = occupancyFraction * STANDARD_SHIFT_MINUTES;

  return {
    loginHours: { value: formatHours(avgLoginMinutes), total: formatHours(totalLoginMinutes) },
    handlingTime: {
      value: formatHours(avgHandlingMinutes),
      pace: formatHours(projectedFullShiftMinutes),
      paceFraction: Math.min(avgHandlingMinutes / STANDARD_SHIFT_MINUTES, 1),
    },
    breakTime: {
      value: formatHours(avgBreakMinutes),
      offLeadPct: avgLoginMinutes ? Math.round((avgBreakMinutes / avgLoginMinutes) * 100) : 0,
    },
    occupancy: { value: Math.round(occupancyFraction * 100), sub: 'Handling ÷ login' },
    breaches: { value: rows.reduce((sum, r) => sum + r.breaches, 0), sub: rangeLabel },
  };
}

// small helper so we can average the already-formatted "Xh Ym" strings
// without re-deriving them from raw events a second time
function hoursLabelToMinutes(label: string): number {
  const hoursMatch = label.match(/(\d+)h/);
  const minutesMatch = label.match(/(\d+)m/);
  const hours = hoursMatch ? Number(hoursMatch[1]) : 0;
  const minutes = minutesMatch ? Number(minutesMatch[1]) : 0;
  return hours * 60 + minutes;
}

export function computeAchtOverall(events: RmActivityEvent[]) {
  const closedCalls = events.filter(isClosedCallTouch);
  const secondsFor = (updatedStatus: UpdatedStatus) =>
    measuredDurations(closedCalls.filter((c) => c.updatedStatus === updatedStatus));

  const build = (updatedStatus: UpdatedStatus, thresholdSeconds: number, thresholdLabel: string) => {
    const seconds = secondsFor(updatedStatus);
    const avgSeconds = average(seconds);
    return {
      value: formatDuration(avgSeconds),
      touches: seconds.length,
      threshold: thresholdLabel,
      fraction: Math.min(avgSeconds / (thresholdSeconds * 1.5), 1),
    };
  };

  return {
    value: formatDuration(average(measuredDurations(closedCalls))),
    touches: closedCalls.length,
    notConnected: build('NOT_CONNECTED', ACHT_THRESHOLDS.notConnected, '≤1m 30s'),
    callBack: build('CALL_BACK', ACHT_THRESHOLDS.callBack, '≤15m 00s'),
    notInterested: build('NOT_INTERESTED', ACHT_THRESHOLDS.notInterested, '≤18m 00s'),
    trial: build('TRIAL_ACTIVATED', ACHT_THRESHOLDS.trial, '≤25m 00s'),
  };
}

export const achtThresholds = ACHT_THRESHOLDS;

// ---- one RM's own shift snapshot (for the lead card carousel) ----

export interface MyShiftDispositionStat {
  value: string;
  touches: number;
  threshold: string;
  fraction: number;
}

export interface MyShiftSnapshot {
  handling: { value: string; pace: string; paceFraction: number };
  breakTime: { value: string; offLeadPct: number };
  notConnected: MyShiftDispositionStat;
  callBack: MyShiftDispositionStat;
  notInterested: MyShiftDispositionStat;
  trial: MyShiftDispositionStat;
}

// Same formulas as buildAdherenceRow/computeAchtOverall above, just scoped to
// one RM's events instead of everyone's — this is what the lead card
// carousel's "Your Shift" panel shows.
export function computeMyShiftSnapshot(events: RmActivityEvent[], rmUserId: string): MyShiftSnapshot {
  const rmEvents = events.filter((e) => e.rmUserId === rmUserId);
  const timing = computeShiftTiming(rmEvents);
  const closedCalls = rmEvents.filter(isClosedCallTouch);

  const handlingMinutes = mergedIntervalMinutes(closedCalls);
  const occupancyFraction = timing.loginMinutes ? handlingMinutes / timing.loginMinutes : 0;
  // same residual as buildAdherenceRow — BREAK_START/BREAK_END aren't
  // written yet, so login-minus-handling is the only real break signal
  const breakMinutes = Math.max(0, timing.loginMinutes - handlingMinutes);

  const buildDispositionStat = (
    updatedStatus: UpdatedStatus,
    thresholdSeconds: number,
    thresholdLabel: string
  ): MyShiftDispositionStat => {
    const seconds = measuredDurations(closedCalls.filter((c) => c.updatedStatus === updatedStatus));
    const avgSeconds = average(seconds);
    return {
      value: formatDuration(avgSeconds),
      touches: seconds.length,
      threshold: thresholdLabel,
      fraction: Math.min(avgSeconds / (thresholdSeconds * 1.5), 1),
    };
  };

  return {
    handling: {
      value: formatHours(handlingMinutes),
      pace: formatHours(occupancyFraction * STANDARD_SHIFT_MINUTES),
      paceFraction: Math.min(handlingMinutes / STANDARD_SHIFT_MINUTES, 1),
    },
    breakTime: {
      value: formatHours(breakMinutes),
      offLeadPct: timing.loginMinutes ? Math.round((breakMinutes / timing.loginMinutes) * 100) : 0,
    },
    notConnected: buildDispositionStat('NOT_CONNECTED', ACHT_THRESHOLDS.notConnected, '≤1m 30s'),
    callBack: buildDispositionStat('CALL_BACK', ACHT_THRESHOLDS.callBack, '≤15m 00s'),
    notInterested: buildDispositionStat('NOT_INTERESTED', ACHT_THRESHOLDS.notInterested, '≤18m 00s'),
    trial: buildDispositionStat('TRIAL_ACTIVATED', ACHT_THRESHOLDS.trial, '≤25m 00s'),
  };
}
